import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ACTIVITY_STATUS_LABEL, WIZARD_STEPS, type ActivityStatus } from '@rat/shared';
import {
  addObservation,
  decideReview,
  deleteAttachmentApi,
  downloadAttachment,
  fetchActivity,
  fetchActivityHistory,
  fetchAttachments,
  fetchOpenObservations,
  fetchVersionDiff,
  respondObservation,
  resolveObservation,
  transitionActivity,
  uploadAttachmentApi,
  type AttachmentRow,
  type ObservationSeverity,
} from '../lib/queries';
import { useAuth } from '../auth/AuthProvider';
import { canIn } from '../auth/access';
import { errorMessage } from '../lib/errors';
import { allowedActivityTransitions } from '../lib/workflowPerms';
import { ActivityStatusPill, PageHeader, QueryState } from '../components/ui';
import { ObservationItem } from '../components/Observations';
import { ActivityStepper } from '../components/ActivityStepper';
import { Breadcrumbs } from '../components/Breadcrumbs';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function ActivityDetail() {
  const { id = '' } = useParams();
  const { access } = useAuth();
  const qc = useQueryClient();

  const validId = UUID_RE.test(id);
  const act = useQuery({
    queryKey: ['activity', id],
    queryFn: () => fetchActivity(id),
    enabled: validId,
  });
  const history = useQuery({ queryKey: ['activity', id, 'history'], queryFn: () => fetchActivityHistory(id) });
  const obs = useQuery({ queryKey: ['activity', id, 'obs'], queryFn: () => fetchOpenObservations(id) });

  const a = act.data as any;
  const status: ActivityStatus | undefined = a?.status;

  // Todo control se decide EN LA UNIDAD de la actividad (un Revisor de la Facultad A no edita la de la B, un
  // usuario de Consulta no envia nada). Solo se ofrece lo que el rol puede hacer; el backend igual revalida.
  const unitId: string | undefined = a?.responsible_unit_id;
  const allowed = useMemo(
    () => (status ? allowedActivityTransitions(access, status, unitId) : []),
    [status, access, unitId],
  );

  // APROBADO/OBSERVADO pasan por review-decide (bloquea aprobar con
  // observaciones obligatorias sin resolver) -- el resto usa el endpoint
  // generico de estado. Ver queries.ts:decideReview.
  const mut = useMutation({
    mutationFn: ({ to, comment }: { to: ActivityStatus; comment?: string }) =>
      to === 'APROBADO' || to === 'OBSERVADO'
        ? decideReview(id, to === 'APROBADO' ? 'approved' : 'observed')
        : transitionActivity(id, to, comment),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['activity', id] });
      void qc.invalidateQueries({ queryKey: ['activity', id, 'obs'] });
      void qc.invalidateQueries({ queryKey: ['activities'] });
    },
  });

  const openObligatory = useMemo(
    () => (obs.data ?? []).filter((o) => o.severity === 'obligatoria' && !o.resolved_at).length,
    [obs.data],
  );

  // Observaciones obligatorias que la UNIDAD aun no responde: bloquean "marcar como corregida" en el backend.
  const unansweredObligatory = useMemo(
    () => (obs.data ?? []).filter((o) => o.severity === 'obligatoria' && (o.state === 'abierta' || o.state === 'reabierta')).length,
    [obs.data],
  );
  // Aprobada o cerrada: el hilo se congela (el backend responde 409 'frozen').
  const frozen = status === 'APROBADO' || status === 'CERRADO';
  // El REVISOR confirma/reabre; la UNIDAD responde. Quien puede resolver no responde (no se contesta a si mismo).
  const canResolve = canIn(access, 'observation.resolve', unitId) && !frozen;
  const canRespond = canIn(access, 'observation.respond', unitId) && !canResolve && !frozen;

  const resolveMut = useMutation({
    mutationFn: ({ obsId, resolved }: { obsId: string; resolved: boolean }) => resolveObservation(obsId, resolved),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['activity', id, 'obs'] }),
  });

  const respondMut = useMutation({
    mutationFn: ({ obsId, body }: { obsId: string; body: string }) => respondObservation(obsId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['activity', id, 'obs'] }),
  });

  if (!validId || (!act.isLoading && !act.error && !a)) {
    return (
      <>
        <PageHeader title="Actividad no encontrada" sub="El recurso no existe o no está dentro de su alcance." />
        <Link className="btn" to="/actividades">Volver a actividades</Link>
      </>
    );
  }

  return (
    <QueryState isLoading={act.isLoading} error={act.error}>
      {!a ? (
        <div className="callout">Actividad no encontrada.</div>
      ) : (
        <>
          <Breadcrumbs items={[{ label: 'Actividades', to: '/actividades' }, { label: a.ref_code || a.title || 'Actividad' }]} />
          <PageHeader
            eyebrow={`${a.ref_code} · ${a.responsible_unit?.name_short ?? ''}`}
            title={a.title || '(sin nombre)'}
            sub={
              <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                <ActivityStatusPill status={a.status} />
                <span className="muted">Completitud {a.completeness_pct}%</span>
              </span>
            }
            actions={
              canIn(access, 'activity.update', unitId) && (
                <Link className="btn" to={`/actividades/${id}/editar`}>
                  Editar
                </Link>
              )
            }
          />

          {/* Las observaciones (incluidas las resueltas) dicen si la actividad paso por "Observado"; mientras cargan, no se afirma nada. */}
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="card__b">
              <ActivityStepper status={a.status} hadObservations={obs.data ? obs.data.length > 0 : undefined} />
            </div>
          </div>

          {a.has_sensitive_data &&
            !(a.legal_bases ?? []).some((lb: any) => lb.legal_basis?.requires_reinforced) && (
              <div className="callout callout--warn" style={{ marginBottom: 14 }}>
                Trata datos sensibles y aún no declara una base de licitud reforzada.
              </div>
            )}

          <div className="split split--aside-narrow">
            <div style={{ display: 'grid', gap: 10 }}>
              <Section title="Finalidad y tratamiento">
                <KV k="Finalidad" v={a.purpose} />
                <KV k="Descripción" v={a.description} />
                <KV k="Origen de los datos" v={a.data_source} />
              </Section>
              <Section title="Titulares y datos">
                <KV
                  k="Categorías de titulares"
                  v={(a.subject_categories ?? []).map((x: any) => x.subject_category?.label).join(' · ')}
                />
                <KV
                  k="Categorías de datos"
                  v={(a.data_categories ?? [])
                    .map((x: any) => (x.is_sensitive ? `${x.data_category?.label} (sensible)` : x.data_category?.label))
                    .join(' · ')}
                />
              </Section>
              <Section title="Base jurídica">
                <KV
                  k="Bases de licitud"
                  v={(a.legal_bases ?? []).map((x: any) => x.legal_basis?.label).join(' · ') || '—'}
                />
              </Section>
              <Section title="Destinatarios · Transferencias · Conservación · Seguridad">
                <KV k="Destinatarios" v={(a.recipients ?? []).map((x: any) => x.name || x.recipient_type?.label).join(' · ')} />
                <KV k="Transferencia internacional" v={a.has_international_transfer ? 'Sí' : 'No'} />
                <KV k="Conservación" v={a.retention_text} />
                <KV k="Medidas de seguridad" v={(a.security_measures ?? []).map((x: any) => x.security_measure?.label).join(' · ')} />
              </Section>
            </div>

            <div>
              <div className="card" style={{ marginBottom: 12 }}>
                <div className="card__b">
                  <div className="eyebrow">Cambiar estado</div>
                  {allowed.length === 0 ? (
                    <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                      No hay transiciones disponibles para su rol.
                    </p>
                  ) : (
                    <TransitionControl
                      allowed={allowed}
                      pending={mut.isPending}
                      error={mut.error as Error | null}
                      openObligatory={openObligatory}
                      unansweredObligatory={unansweredObligatory}
                      onGo={(to, comment) => mut.mutate({ to, comment })}
                    />
                  )}
                </div>
              </div>

              <div className="card" style={{ marginBottom: 12 }}>
                <div className="card__b">
                  <div className="eyebrow">Observaciones</div>
                  {(obs.data ?? []).length === 0 ? (
                    <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>Ninguna.</p>
                  ) : (
                    <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
                      {(obs.data ?? []).map((o) => (
                        <ObservationItem
                          key={o.id}
                          o={o}
                          busy={resolveMut.isPending}
                          onToggle={canResolve ? (resolved) => resolveMut.mutate({ obsId: o.id, resolved }) : undefined}
                          onRespond={
                            canRespond && (o.state === 'abierta' || o.state === 'reabierta')
                              ? (body) => respondMut.mutate({ obsId: o.id, body })
                              : undefined
                          }
                          respondPending={respondMut.isPending}
                        />
                      ))}
                    </div>
                  )}

                  {/* Antes estos errores (p. ej. 'frozen') se perdian en silencio. */}
                  {(resolveMut.error || respondMut.error) && (
                    <div className="callout callout--crit" style={{ marginTop: 8 }}>
                      {((resolveMut.error ?? respondMut.error) as Error).message}
                    </div>
                  )}

                  {canRespond && a.status === 'OBSERVADO' && unansweredObligatory > 0 && (
                    <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                      Responda {unansweredObligatory === 1 ? 'la observación obligatoria' : 'las ' + unansweredObligatory + ' observaciones obligatorias'}{' '}
                      para poder corregir y reenviar la actividad.
                    </p>
                  )}

                  {a.status === 'EN_REVISION' && canIn(access, 'observation.create', unitId) && (
                    <AddObservationForm
                      activityId={id}
                      onAdded={() => void qc.invalidateQueries({ queryKey: ['activity', id, 'obs'] })}
                    />
                  )}

                  {(a.status === 'OBSERVADO' || a.status === 'CORREGIDO') &&
                    canIn(access, 'activity.update', unitId) && (
                      <Link className="btn btn--primary" to={`/actividades/${id}/editar`} style={{ marginTop: 10 }}>
                        Corregir y reenviar
                      </Link>
                    )}
                </div>
              </div>

              <div className="card" style={{ marginBottom: 12 }}>
                <div className="card__b">
                  <div className="eyebrow">Adjuntos</div>
                  <AttachmentsPanel id={id} canWrite={canIn(access, 'activity.update', unitId)} />
                </div>
              </div>

              <div className="card">
                <div className="card__b">
                  <div className="eyebrow">Versiones</div>
                  <VersionHistory id={id} versions={history.data ?? []} />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </QueryState>
  );
}

function formatBytes(n: number | null): string {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Misma lectura que la actividad (siempre visible si se pudo cargar la
 * actividad); subir/borrar se gatea con 'activity.update.own_unit', el
 * mismo permiso que "Corregir y reenviar" -- el backend revalida igual
 * (ver src/attachments.ts, misma autorizacion que editar la actividad).
 */
function AttachmentsPanel({ id, canWrite }: { id: string; canWrite: boolean }) {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['activity', id, 'attachments'], queryFn: () => fetchAttachments(id) });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['activity', id, 'attachments'] });

  const uploadMut = useMutation({
    mutationFn: (file: File) => uploadAttachmentApi(id, file),
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: (e) => setError(errorMessage(e, 'No se pudo subir el archivo.')), // p. ej. too_large / empty_file
  });

  async function onDownload(att: AttachmentRow) {
    try {
      await downloadAttachment(id, att.id, att.filename);
    } catch (e) {
      setError(errorMessage(e, 'No se pudo descargar el archivo.'));
    }
  }

  async function onDelete(att: AttachmentRow) {
    setBusyId(att.id);
    setError(null);
    try {
      await deleteAttachmentApi(id, att.id);
      void invalidate();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo eliminar el archivo.'));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      {(list.data ?? []).length === 0 ? (
        <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          Ningún adjunto.
        </p>
      ) : (
        <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
          {(list.data ?? []).map((att) => (
            <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
              <button
                type="button"
                className="btn btn--ghost"
                style={{ flex: 1, justifyContent: 'flex-start', padding: '4px 8px', minWidth: 0, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                onClick={() => onDownload(att)}
                title="Descargar"
              >
                {att.filename}
              </button>
              <span className="muted mono" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                {formatBytes(att.size_bytes)}
              </span>
              {canWrite && (
                <button
                  type="button"
                  className="btn btn--ghost"
                  style={{ color: 'var(--critical)', padding: '4px 8px' }}
                  disabled={busyId === att.id}
                  onClick={() => onDelete(att)}
                >
                  {busyId === att.id ? '…' : 'Borrar'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {canWrite && (
        <label className="btn" style={{ marginTop: 10, display: 'inline-flex', cursor: uploadMut.isPending ? 'not-allowed' : 'pointer' }}>
          {uploadMut.isPending ? 'Subiendo…' : 'Subir archivo'}
          <input
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.webp"
            style={{ display: 'none' }}
            disabled={uploadMut.isPending}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) uploadMut.mutate(file);
            }}
          />
        </label>
      )}
      {error && (
        <div className="errmsg" style={{ marginTop: 6 }}>
          {error}
        </div>
      )}
    </>
  );
}

function VersionHistory({
  id,
  versions,
}: {
  id: string;
  versions: Array<{ id: string; version_no: number; reason: string; created_at: string }>;
}) {
  const [pair, setPair] = useState<[number, number] | null>(null);
  const diff = useQuery({
    queryKey: ['activity', id, 'diff', pair?.[0], pair?.[1]],
    queryFn: () => fetchVersionDiff(id, pair![0], pair![1]),
    enabled: !!pair,
  });

  const REASON: Record<string, string> = { submit: 'envío a revisión', approve: 'aprobación', manual: 'guardado manual', restore: 'restauración' };

  if (versions.length === 0) return <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>Sin versiones aún.</p>;

  return (
    <div style={{ marginTop: 6 }}>
      {versions.map((v, i) => {
        const prev = versions[i + 1];
        return (
          <div key={v.id} style={{ fontSize: 12, marginTop: 6, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="mono">v{v.version_no}</span>
            <span className="muted">{REASON[v.reason] ?? v.reason} · {new Date(v.created_at).toLocaleDateString('es-CL')}</span>
            {prev && (
              <button
                className="btn btn--ghost"
                style={{ fontSize: 11, padding: '1px 6px' }}
                onClick={() => setPair(pair && pair[0] === prev.version_no && pair[1] === v.version_no ? null : [prev.version_no, v.version_no])}
              >
                {pair && pair[0] === prev.version_no && pair[1] === v.version_no ? 'ocultar' : `↔ v${prev.version_no}`}
              </button>
            )}
          </div>
        );
      })}

      {pair && (
        <div style={{ marginTop: 8, borderTop: '1px solid var(--border)', paddingTop: 8, fontSize: 12 }}>
          <b>v{pair[0]} → v{pair[1]}</b>
          {diff.isLoading && <p className="muted">Comparando…</p>}
          {diff.data && diff.data.scalar.length === 0 && diff.data.collections.length === 0 && (
            <p className="muted">Sin cambios en los campos comparados.</p>
          )}
          {diff.data?.scalar.map((c) => (
            <div key={c.field} style={{ marginTop: 4 }}>
              <span className="muted">{c.field}: </span>
              <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>{String(c.a ?? '—')}</span>{' → '}
              <b>{String(c.b ?? '—')}</b>
            </div>
          ))}
          {diff.data?.collections.map((c) => (
            <div key={c.name} style={{ marginTop: 4 }}>
              <span className="muted">{c.name}: </span>{c.a} → <b>{c.b}</b> ítem(s)
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <details className="card" open>
      <summary style={{ padding: '12px 14px', fontWeight: 600, fontSize: 13.5, cursor: 'pointer' }}>
        {title}
      </summary>
      <div style={{ padding: 14, display: 'grid', gap: 10, borderTop: '1px solid var(--border)' }}>
        {children}
      </div>
    </details>
  );
}

function KV({ k, v }: { k: string; v?: string | null }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 12, fontSize: 13 }}>
      <span className="muted" style={{ fontSize: 12 }}>{k}</span>
      <span>{v || <span className="muted">—</span>}</span>
    </div>
  );
}

function TransitionControl({
  allowed,
  pending,
  error,
  openObligatory,
  unansweredObligatory,
  onGo,
}: {
  allowed: ActivityStatus[];
  pending: boolean;
  error: Error | null;
  openObligatory: number;
  unansweredObligatory: number;
  onGo: (to: ActivityStatus, comment?: string) => void;
}) {
  const [to, setTo] = useState<ActivityStatus>(allowed[0]!);
  const [comment, setComment] = useState('');
  const needsComment = to === 'OBSERVADO' || to === 'EN_REVISION';
  const blockedByObligatory = to === 'APROBADO' && openObligatory > 0;
  // El backend rechaza CORREGIDO con observaciones obligatorias sin responder (observations_pending_response).
  const blockedByUnanswered = to === 'CORREGIDO' && unansweredObligatory > 0;
  return (
    <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
      <select
        value={to}
        onChange={(e) => setTo(e.target.value as ActivityStatus)}
        style={{ padding: '7px 9px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--ground)', color: 'var(--ink)' }}
      >
        {allowed.map((s) => (
          <option key={s} value={s}>
            {ACTIVITY_STATUS_LABEL[s]}
          </option>
        ))}
      </select>
      <input
        placeholder={needsComment ? 'Comentario (obligatorio)' : 'Comentario (opcional)'}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        style={{ padding: '7px 9px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--ground)', color: 'var(--ink)' }}
      />
      {blockedByObligatory && (
        <span className="muted" style={{ fontSize: 12 }}>
          Hay {openObligatory} observación(es) obligatoria(s) sin resolver.
        </span>
      )}
      {blockedByUnanswered && (
        <span className="muted" style={{ fontSize: 12 }}>
          Responda {unansweredObligatory} observación(es) obligatoria(s) antes de marcar la actividad como corregida.
        </span>
      )}
      <button
        className="btn btn--primary"
        disabled={pending || blockedByObligatory || blockedByUnanswered || (needsComment && !comment.trim())}
        onClick={() => onGo(to, comment || undefined)}
      >
        {pending ? 'Aplicando…' : 'Aplicar transición'}
      </button>
      {error && <div className="callout callout--crit">{error.message}</div>}
    </div>
  );
}

function AddObservationForm({ activityId, onAdded }: { activityId: string; onAdded: () => void }) {
  const [field, setField] = useState('general');
  const [severity, setSeverity] = useState<ObservationSeverity>('obligatoria');
  const [text, setText] = useState('');

  const mut = useMutation({
    mutationFn: () => addObservation(activityId, field, severity, text.trim()),
    onSuccess: () => {
      setText('');
      onAdded();
    },
  });

  return (
    <div className="obs-form" style={{ borderTop: '1px solid var(--border)', marginTop: 10, paddingTop: 10 }}>
      <select
        value={field}
        onChange={(e) => setField(e.target.value)}
      >
        <option value="general">General</option>
        {WIZARD_STEPS.filter((s) => s.key !== 'revision').map((s) => (
          <option key={s.key} value={s.key}>
            {s.label}
          </option>
        ))}
      </select>
      <select
        value={severity}
        onChange={(e) => setSeverity(e.target.value as ObservationSeverity)}
      >
        <option value="obligatoria">Obligatoria (bloquea la aprobación)</option>
        <option value="sugerida">Sugerida</option>
      </select>
      <textarea
        rows={3}
        placeholder="Describa qué debe corregirse"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <button
        className="btn"
        disabled={text.trim().length < 3 || mut.isPending}
        onClick={() => mut.mutate()}
      >
        {mut.isPending ? 'Agregando…' : '+ Agregar observación'}
      </button>
      {mut.error && <div className="callout callout--crit">{(mut.error as Error).message}</div>}
    </div>
  );
}
